/**
 * NotificationManager
 *
 * 웹 푸시 알림 시스템의 중앙 관리자
 * Service Worker 등록, FCM 토큰 관리, 알림 권한 처리를 담당합니다.
 */

import { getMessaging, onMessage } from 'firebase/messaging';
import badgeManager from './BadgeManager';
import fcmTokenHandler from './FCMTokenHandler';

class NotificationManager {
  constructor() {
    this.messaging = null;
    this.serviceWorkerRegistration = null;
    this.vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    this.onMessageCallback = null;
    this.onTokenCallback = null;
  }

  /**
   * Service Worker 등록
   */
  async registerServiceWorker() {
    try {
      if (!('serviceWorker' in navigator)) {
        console.warn('Service Worker를 지원하지 않는 브라우저입니다.');
        return null;
      }

      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      this.serviceWorkerRegistration = registration;
      return registration;
    } catch (error) {
      console.error('Service Worker 등록 실패:', error);
      return null;
    }
  }

  /**
   * 알림 권한 요청
   */
  async requestPermission() {
    try {
      if (!('Notification' in window)) {
        console.warn('Notification API를 지원하지 않는 브라우저입니다.');
        return null;
      }

      if (Notification.permission === 'granted') {
        return 'granted';
      }

      if (Notification.permission === 'denied') {
        console.warn('알림 권한이 거부되었습니다. 브라우저 설정에서 변경해주세요.');
        return 'denied';
      }

      const permission = await Notification.requestPermission();
      return permission;
    } catch (error) {
      console.error('알림 권한 요청 실패:', error);
      return null;
    }
  }

  /**
   * FCM 초기화 및 토큰 생성
   */
  async initializeFCM(firebaseApp) {
    try {
      if (!this.vapidKey) {
        console.error('VAPID 키가 설정되지 않았습니다.');
        return null;
      }

      if (!this.serviceWorkerRegistration) {
        console.warn('Service Worker가 등록되지 않았습니다.');
        return null;
      }

      if (Notification.permission !== 'granted') {
        console.warn('알림 권한이 허용되지 않았습니다.');
        return null;
      }

      this.messaging = getMessaging(firebaseApp);

      // FCMTokenHandler를 사용하여 토큰 생성
      const token = await fcmTokenHandler.generateToken(
        this.messaging,
        this.vapidKey,
        this.serviceWorkerRegistration,
      );

      if (token) {
        if (this.onTokenCallback) {
          this.onTokenCallback(token);
        }
        return token;
      }

      return null;
    } catch (error) {
      console.error('FCM 초기화 실패:', error);
      return null;
    }
  }

  /**
   * 포그라운드 메시지 리스너 설정
   */
  setupForegroundMessageListener(callback, options = {}) {
    if (!this.messaging) {
      console.warn('Firebase Messaging이 초기화되지 않았습니다.');
      return;
    }

    const { updateBadge = true } = options;
    this.onMessageCallback = callback;

    onMessage(this.messaging, (payload) => {
      if (updateBadge) {
        const unreadCount = Number(payload?.data?.unreadCount);
        if (unreadCount) {
          this.setBadgeCount(unreadCount);
        } else {
          this.incrementBadge();
        }
      }

      if (this.onMessageCallback) {
        this.onMessageCallback(payload, options);
      }
    });
  }

  incrementBadge() {
    badgeManager.increment();
  }

  setBadgeCount(count) {
    badgeManager.setCount(count);
  }

  decrementBadge() {
    badgeManager.decrement();
  }

  resetBadge() {
    badgeManager.reset();
  }

  getPermissionStatus() {
    return ('Notification' in window) ? Notification.permission : null;
  }

  // TODO 로그아웃 시 토큰 삭제
  async deleteToken() {
    try {
      if (!this.messaging) return false;

      // FCMTokenHandler를 사용하여 토큰 삭제
      const result = await fcmTokenHandler.deleteTokenFromBackend();

      // Firebase에서도 토큰 삭제
      const { deleteToken } = await import('firebase/messaging');
      await deleteToken(this.messaging);
      return result;
    } catch (error) {
      console.error('FCM 토큰 삭제 실패:', error);
      return false;
    }
  }

  checkBrowserSupport() {
    return {
      serviceWorker: 'serviceWorker' in navigator,
      notification: 'Notification' in window,
      pushManager: 'PushManager' in window,
    };
  }

  setupTokenRefreshListener(callback) {
    this.onTokenCallback = callback;
  }
}

// 싱글톤 인스턴스 생성
const notificationManager = new NotificationManager();

export default notificationManager;
